import React, { useEffect, useState, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';

/**
 * StudioSlideshow
 * Auto-advancing image slideshow with arrow controls, dot navigation, and pause-on-hover.
 * Respects prefers-reduced-motion (auto-play disabled).
 *
 * Props:
 *  - images: [{ src, alt }]
 *  - intervalMs: number (default 5000)
 *  - aspectClass: Tailwind aspect utility (default 'aspect-[4/3]')
 */
export function StudioSlideshow({
  images = [],
  intervalMs = 5000,
  aspectClass = 'aspect-[4/3] sm:aspect-[16/10]',
  testId = 'studio-slideshow',
}) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const timerRef = useRef(null);
  const total = images.length;

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handle = () => setReducedMotion(mq.matches);
    handle();
    mq.addEventListener?.('change', handle);
    return () => mq.removeEventListener?.('change', handle);
  }, []);

  const goTo = useCallback((i) => setIndex(((i % total) + total) % total), [total]);
  const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [total]);

  useEffect(() => {
    if (paused || reducedMotion || total <= 1) return undefined;
    timerRef.current = setInterval(next, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [paused, reducedMotion, intervalMs, next, total]);

  // Keyboard nav when slideshow is focused
  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
  };

  if (!total) return null;

  return (
    <div
      className="relative"
      role="region"
      aria-roledescription="carousel"
      aria-label="Studio photo gallery"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={onKeyDown}
      tabIndex={0}
      data-testid={testId}
    >
      {/* Frame */}
      <div className={`surface overflow-hidden relative ${aspectClass}`}>
        {images.map((img, i) => (
          <img
            key={img.src}
            src={img.src}
            alt={img.alt}
            loading={i === 0 ? 'eager' : 'lazy'}
            fetchPriority={i === 0 ? 'high' : 'auto'}
            aria-hidden={i !== index}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-out ${
              i === index ? 'opacity-100' : 'opacity-0'
            }`}
            data-testid={`${testId}-image-${i}`}
          />
        ))}

        {/* Bottom gradient for legibility */}
        <div
          className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
          style={{ background: 'linear-gradient(to top, rgba(9,11,14,0.85), rgba(9,11,14,0))' }}
          aria-hidden="true"
        />

        {/* Prev / Next arrows */}
        <button
          type="button"
          onClick={prev}
          aria-label="Previous photo"
          className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full border border-[color:var(--ft-border)] bg-[rgba(9,11,14,0.65)] backdrop-blur-sm text-white flex items-center justify-center hover:bg-[rgba(9,11,14,0.85)] hover:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition-colors"
          data-testid={`${testId}-prev`}
        >
          <ChevronLeft size={22} />
        </button>
        <button
          type="button"
          onClick={next}
          aria-label="Next photo"
          className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full border border-[color:var(--ft-border)] bg-[rgba(9,11,14,0.65)] backdrop-blur-sm text-white flex items-center justify-center hover:bg-[rgba(9,11,14,0.85)] hover:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition-colors"
          data-testid={`${testId}-next`}
        >
          <ChevronRight size={22} />
        </button>

        {/* Play/Pause toggle */}
        <button
          type="button"
          onClick={() => setPaused((p) => !p)}
          aria-label={paused ? 'Play slideshow' : 'Pause slideshow'}
          className="absolute top-3 right-3 h-9 w-9 rounded-full border border-[color:var(--ft-border)] bg-[rgba(9,11,14,0.55)] backdrop-blur-sm text-white flex items-center justify-center hover:bg-[rgba(9,11,14,0.85)] hover:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition-colors"
          data-testid={`${testId}-toggle`}
        >
          {paused ? <Play size={14} fill="currentColor" /> : <Pause size={14} fill="currentColor" />}
        </button>

        {/* Counter */}
        <div
          className="absolute bottom-3 left-3 px-2.5 py-1 rounded-full text-xs font-medium bg-[rgba(9,11,14,0.65)] backdrop-blur-sm border border-[color:var(--ft-border)] text-white"
          aria-live="polite"
          data-testid={`${testId}-counter`}
        >
          {index + 1} / {total}
        </div>
      </div>

      {/* Dot navigation */}
      <div
        className="mt-4 flex items-center justify-center gap-2"
        role="tablist"
        aria-label="Select photo"
      >
        {images.map((_, i) => (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={i === index}
            aria-label={`Show photo ${i + 1}`}
            onClick={() => goTo(i)}
            data-testid={`${testId}-dot-${i}`}
            className={`h-2 rounded-full transition-[width,background-color] duration-200 ${
              i === index
                ? 'w-7 bg-accent'
                : 'w-2 bg-[color:var(--ft-border)] hover:bg-[color:var(--ft-muted)]'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export default StudioSlideshow;
